import { consoleRoutes, type DefaultWorkflowKey } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Icon } from "@sagot/ui";
import { usePermissions } from "../../../../session/permissions";
import { useWorkspaceContext } from "../../../../session/workspace-context";
import type { WorkflowOut } from "../types";
import { useDefaultWorkflowList } from "./use-defaults";

const X = "engage.workflows.defaults.cross";

/** The default with this key, when the member may read workflows (and the list has arrived). */
export function useDefaultByKey(systemKey: string): { workflow: WorkflowOut | undefined; canView: boolean } {
  const canView = usePermissions().can("workflows.view");
  const list = useDefaultWorkflowList(canView);
  return { workflow: list.data?.find((row) => row.system_key === systemKey), canView };
}

/** On the notification preferences: which events notify the team is the default workflows' business; how a member receives them is theirs. */
export function NotificationsWorkflowsNote() {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const canView = usePermissions().can("workflows.view");
  return (
    <p className="flex items-start gap-2 rounded-md bg-info-soft p-3 text-xs text-fg-muted" data-testid="notifications-workflows-note">
      <Icon name="workflow" className="mt-0.5 size-4 shrink-0 text-primary-ink" />
      <span>
        {t(`${X}.notifications`)}
        {canView ? (
          <>
            {" "}
            <AppLink className="font-semibold text-primary-ink underline" href={consoleRoutes.workflows(summary.slug)}>{t(`${X}.notificationsLink`)}</AppLink>
          </>
        ) : null}
      </span>
    </p>
  );
}

interface DefaultWorkflowNoteProps {
  systemKey: DefaultWorkflowKey;
  /** The sentence, with `{{name}}`: which default uses the settings next to it. */
  textKey: string;
}

/**
 * A note on a settings page whose values a default workflow now uses: it names the workflow and links to it, and warns when it is
 * paused (the settings are then not used by anything). Members who cannot read workflows still get the sentence, without the link.
 */
export function DefaultWorkflowNote({ systemKey, textKey }: DefaultWorkflowNoteProps) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const { workflow, canView } = useDefaultByKey(systemKey);
  const name = workflow?.name ?? t(`${X}.names.${systemKey}`);
  return (
    <div className="space-y-2" data-testid="default-note" data-key={systemKey}>
      <p className="flex items-start gap-2 rounded-md bg-info-soft p-3 text-xs text-fg-muted">
        <Icon name="workflow" className="mt-0.5 size-4 shrink-0 text-primary-ink" />
        <span>
          {t(textKey, { name })}
          {workflow && canView ? (
            <>
              {" "}
              <AppLink className="font-semibold text-primary-ink underline" href={consoleRoutes.workflow(summary.slug, workflow.id)}>{t(`${X}.open`)}</AppLink>
            </>
          ) : null}
        </span>
      </p>
      {workflow?.state === "paused" ? (
        <p className="flex items-start gap-2 rounded-md border border-warning bg-warning-soft p-3 text-xs font-semibold" role="status" data-testid="default-note-paused">
          <Icon name="pause" className="mt-0.5 size-4 shrink-0 text-warning" />
          <span>{t(`${X}.paused`, { name, impact: workflow.pause_impact ?? "" })}</span>
        </p>
      ) : null}
    </div>
  );
}
